import type { CustomAttributeData } from '@risksmart-app/domain/src/types/common.types';
import type {
  Appetite_Type_Enum,
  UpdateAppetiteMutation,
  UpdateAppetiteMutationVariables,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useTRPC } from '@/utils/trpc';

export type UpdateAppetiteTRPCInput = Omit<
  UpdateAppetiteMutationVariables,
  'OriginalTimestamp' | 'AppetiteType' | 'CustomAttributeData'
> & {
  AppetiteType: Appetite_Type_Enum;
  CustomAttributeData?: CustomAttributeData | null;
};

const mapTrpcResponseToGraphQL = (): UpdateAppetiteMutation => {
  return {
    update_appetite: {
      __typename: 'appetite_mutation_response',
      affected_rows: 1,
    },
  };
};

export const useUpdateAppetiteTRPC = () => {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    ...trpc.frontend.appetite.update.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.appetite.register.queryKey(),
        });
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.appetite.getById.queryKey(),
        });
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.appetite.appetitesByRiskId.queryKey(),
        });
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.appetite.activeAppetitesByParentId.queryKey(),
        });
        await queryClient.invalidateQueries({
          queryKey:
            trpc.frontend.appetite.getAppetitesGroupedByImpact.queryKey(),
        });
      },
    }),
  });

  return {
    updateAppetite: async (
      variables: UpdateAppetiteTRPCInput
    ): Promise<UpdateAppetiteMutation> => {
      await mutation.mutateAsync(variables);

      return mapTrpcResponseToGraphQL();
    },
    loading: mutation.isPending,
    error: mutation.error,
  };
};
